/* Usage telemetry (review §7.1) — `track(page, feature, meta)`.
 *
 * Salem 2026-09-21: *"We have no idea which parts of the app people actually use."* Every
 * route change and a handful of named actions (Live Assist opened, the Oracle asked, the
 * toolkit asked) are queued here and posted to /api/telemetry in one batch every 15 seconds
 * or when the tab is hidden — never per click, never blocking anything, never surfacing an
 * error. The nightly summary on Intelligence Health is built from these rows.
 *
 * Usage:
 *   import { track } from "../telemetry";
 *   track("/calls/123", "live_assist.open", { callId: 123 });
 *   track(location.pathname, "view");            // App.jsx does this on every route change
 *
 * What is NOT sent: anything a person typed. `meta` is a few short scalars at most.
 */
import { api, getToken } from "./api";

const FLUSH_MS = 15_000;
const MAX_QUEUE = 200;
let queue = [];
let timer = null;

const at = () => new Date().toISOString();

function clean(meta) {
  if (!meta || typeof meta !== "object") return undefined;
  const out = {};
  let n = 0;
  for (const [k, v] of Object.entries(meta)) {
    if (n++ >= 8) break;
    if (v == null) continue;
    out[String(k).slice(0, 40)] = (typeof v === "number" || typeof v === "boolean") ? v : String(v).slice(0, 120);
  }
  return out;
}

/** Queue one event. Safe to call anywhere, any time; drops silently when signed out. */
export function track(page, feature = "view", meta) {
  try {
    if (!getToken()) return;
    queue.push({ page: String(page || window.location.pathname).slice(0, 120),
                 feature: String(feature || "view").slice(0, 80), meta: clean(meta), at: at() });
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    if (!timer) timer = setTimeout(flush, FLUSH_MS);
  } catch { /* telemetry never throws */ }
}

/** Send whatever is queued. `beacon` = the tab is going away, so use sendBeacon if we can. */
export function flush(beacon = false) {
  if (timer) { clearTimeout(timer); timer = null; }
  if (!queue.length || !getToken()) return;
  const events = queue;
  queue = [];
  try {
    if (beacon && navigator.sendBeacon) {
      // sendBeacon cannot carry an Authorization header, so the token travels in the body;
      // the server accepts either. Fall through to fetch if the browser refuses the beacon.
      const blob = new Blob([JSON.stringify({ events, token: getToken() })], { type: "application/json" });
      if (navigator.sendBeacon((api.base || "") + "/api/telemetry", blob)) return;
    }
    api.post("/api/telemetry", { events }).catch(() => {});
  } catch { /* ignore */ }
}

function onVisibility() {
  if (document.visibilityState === "hidden") flush(true);
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("pagehide", () => flush(true));
}

export default track;
